/**
 * Knowledge Base Page — Redesigned
 *
 * Two-layer architecture:
 * 1. STATIC CURATED LAYER: Workflow-organized guides with "Try it" prompts
 *    that teach users how to interact with Trendy effectively
 * 2. DYNAMIC LEARNED LAYER: Business rules, customer notes, and pricing
 *    policies from the database (auto-learned from conversations)
 *
 * Design principles:
 * - Organize by workflow/outcome, not by Epicor module
 * - Surface example prompts so users learn by doing
 * - "Try it" buttons navigate to chat and auto-send the prompt
 * - Onboarding lane for new users
 * - Company knowledge (dynamic) lives below curated content
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  BookOpen, Search, Loader2, Clock, User, Tag, ChevronDown,
  ChevronRight, FileText, AlertCircle, ArrowRight, Lightbulb,
  MessageSquare, Sparkles, ArrowLeft,
  // Sidebar nav icons (Notion-style IA — added with Phase 4)
  Building2, Truck, Package, Briefcase, UserCircle, Folder,
  Database, Activity, ListChecks, Network, Shield,
} from 'lucide-react'
import { useTools } from '../hooks/useTools'
import { useAuth } from '../hooks/useAuth'
import {
  ONBOARDING_CARDS,
  WORKFLOW_SECTIONS,
  PROMPTING_TIPS,
  SECTION_COLORS,
} from '../config/kbWorkflows'
import DataSourcesTab from '../components/knowledge/DataSourcesTab'
import SourceSearchResults from '../components/knowledge/SourceSearchResults'
import AuditTab from '../components/knowledge/AuditTab'
import BAQsTab from '../components/knowledge/BAQsTab'

// Icon + label per entity_type for the new Entities tab. Mirrors the map
// used on EntityHubPage so the visual language is consistent across the
// list view and the per-entity detail view.
const ENTITY_TYPE_META = {
  customer: { label: 'Customer', icon: Building2,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconText: 'text-emerald-600 dark:text-emerald-400' },
  vendor: { label: 'Vendor', icon: Truck,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconText: 'text-orange-600 dark:text-orange-400' },
  part: { label: 'Part', icon: Package,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconText: 'text-amber-600 dark:text-amber-400' },
  job: { label: 'Job', icon: Briefcase,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconText: 'text-blue-600 dark:text-blue-400' },
  employee: { label: 'Employee', icon: UserCircle,
    iconBg: 'bg-violet-100 dark:bg-violet-900/30',
    iconText: 'text-violet-600 dark:text-violet-400' },
  project: { label: 'Project', icon: Folder,
    iconBg: 'bg-rose-100 dark:bg-rose-900/30',
    iconText: 'text-rose-600 dark:text-rose-400' },
}

// ─── Category config for dynamic KB entries ─────────────────────────────────

const CATEGORY_CONFIG = {
  business_rule: {
    label: 'Business Rules',
    description: 'Company-wide rules, policies, and standard operating procedures',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  customer_note: {
    label: 'Customer Notes',
    description: 'Facts, preferences, and context about specific customers',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  part_note: {
    label: 'Part Notes',
    description: 'Manufacturing details, material specs, and part-specific knowledge',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
  },
  pricing_note: {
    label: 'Pricing Notes',
    description: 'Pricing guidelines, discount policies, and rate structures',
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
  },
  entity_relationship: {
    label: 'Entity Relationships',
    description: 'Connections between customers, vendors, parts, and other entities',
    color: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-900/20',
    border: 'border-gray-200 dark:border-gray-700',
  },
}

// ─── Utility functions ──────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 30) return `${days}d ago`
  return formatDate(dateStr)
}

// ─── "Try it" navigation to chat ────────────────────────────────────────────

function useTryInChat() {
  const navigate = useNavigate()
  return useCallback((prompt) => {
    sessionStorage.setItem('kb_try_prompt', prompt)
    navigate('/')
  }, [navigate])
}

// ─── Components ─────────────────────────────────────────────────────────────

function OnboardingCards({ onTry }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('kb_onboarding_dismissed') === 'true' } catch { return false }
  })

  if (dismissed) return null

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-amtrend-blue" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            New to Trendy?
          </h2>
        </div>
        <button
          onClick={() => {
            setDismissed(true)
            try { localStorage.setItem('kb_onboarding_dismissed', 'true') } catch {}
          }}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          Dismiss
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ONBOARDING_CARDS.map((card) => (
          <button
            key={card.id}
            onClick={() => onTry(card.example)}
            className="group text-left p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised hover:border-amtrend-blue/40 hover:shadow-sm transition-all"
          >
            <div className="text-2xl mb-2">{card.icon}</div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {card.title}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
              {card.description}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-amtrend-blue opacity-0 group-hover:opacity-100 transition-opacity">
              <MessageSquare size={12} />
              <span>Try it</span>
              <ArrowRight size={12} />
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function TryItButton({ prompt, onTry }) {
  return (
    <button
      onClick={() => onTry(prompt)}
      className="group flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-surface-dark-raised border border-gray-100 dark:border-gray-700 hover:border-amtrend-blue/40 hover:bg-blue-50/50 dark:hover:bg-amtrend-blue/10 transition-all text-sm"
    >
      <MessageSquare size={14} className="text-gray-400 group-hover:text-amtrend-blue flex-shrink-0 transition-colors" />
      <span className="text-gray-700 dark:text-gray-300 flex-1 truncate">&ldquo;{prompt}&rdquo;</span>
      <ArrowRight size={14} className="text-gray-300 group-hover:text-amtrend-blue flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all" />
    </button>
  )
}

function WorkflowArticle({ article, onTry }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors"
      >
        <div className="text-left">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {article.title}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {article.description}
          </p>
        </div>
        {expanded
          ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0 ml-2" />
          : <ChevronRight size={16} className="text-gray-400 flex-shrink-0 ml-2" />
        }
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-50 dark:border-gray-800 pt-2">
          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Ask Trendy
          </p>
          {article.prompts.map((prompt, i) => (
            <TryItButton key={i} prompt={prompt} onTry={onTry} />
          ))}
          {article.tips && (
            <div className="flex items-start gap-2 mt-2 p-2.5 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30">
              <Lightbulb size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300/80">{article.tips}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function WorkflowSection({ section, onTry, defaultExpanded = false }) {
  const [collapsed, setCollapsed] = useState(!defaultExpanded)
  const colors = SECTION_COLORS[section.color] || SECTION_COLORS.slate

  return (
    <div className="mb-4">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={`w-full flex items-center gap-3 p-3 rounded-xl ${colors.bg} border ${colors.border} hover:opacity-90 transition-opacity`}
      >
        <span className="text-xl flex-shrink-0">{section.icon}</span>
        <div className="flex-1 text-left min-w-0">
          <h3 className={`font-semibold text-sm ${colors.text}`}>
            {section.title}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
            {section.description}
          </p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge} flex-shrink-0`}>
          {section.articles.length}
        </span>
        {collapsed
          ? <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
          : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
        }
      </button>
      {!collapsed && (
        <div className="mt-2 space-y-2 pl-2">
          {section.articles.map((article, i) => (
            <WorkflowArticle key={i} article={article} onTry={onTry} />
          ))}
        </div>
      )}
    </div>
  )
}

function PromptingTips() {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-3"
      >
        <Lightbulb size={16} className="text-amber-500" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Tips for Better Results
        </h2>
        {expanded
          ? <ChevronDown size={14} className="text-gray-400" />
          : <ChevronRight size={14} className="text-gray-400" />
        }
      </button>
      {expanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PROMPTING_TIPS.map((tip, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-surface-dark-raised"
            >
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {tip.title}
              </h4>
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <span className="text-red-400 text-xs mt-0.5">✗</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 line-through">{tip.bad}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 text-xs mt-0.5">✓</span>
                  <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{tip.good}</span>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">{tip.why}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Dynamic KB Entry (from database) ───────────────────────────────────────

function KBEntry({ entry }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = (entry.value || '').length > 200

  return (
    <div className="border border-gray-100 dark:border-gray-800 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
            {entry.key}
          </h4>
          <div className={`mt-1 text-sm text-gray-600 dark:text-gray-400 ${!expanded && isLong ? 'line-clamp-3' : ''}`}>
            {entry.value}
          </div>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1 text-xs text-amtrend-blue hover:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
        {entry.created_by && (
          <span className="flex items-center gap-1">
            <User size={12} />
            {entry.created_by}
          </span>
        )}
        {entry.source && entry.source !== 'manual' && (
          <span className="flex items-center gap-1">
            <Tag size={12} />
            {entry.source}
          </span>
        )}
        {entry.entity_type && entry.entity_id && (
          <span className="flex items-center gap-1">
            <FileText size={12} />
            {entry.entity_type} #{entry.entity_id}
          </span>
        )}
        {entry.updated_at && (
          <span className="flex items-center gap-1" title={new Date(entry.updated_at).toLocaleString()}>
            <Clock size={12} />
            Updated {timeAgo(entry.updated_at)}
          </span>
        )}
        {entry.created_at && entry.created_at !== entry.updated_at && (
          <span className="text-gray-400 dark:text-gray-600" title={new Date(entry.created_at).toLocaleString()}>
            Created {formatDate(entry.created_at)}
          </span>
        )}
      </div>
    </div>
  )
}

function DynamicKBSection({ category, entries, config }) {
  const [collapsed, setCollapsed] = useState(true)

  return (
    <div className="mb-4">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={`w-full flex items-center gap-3 p-3 rounded-lg ${config.bg} border ${config.border} hover:opacity-90 transition-opacity`}
      >
        <div className="flex-1 text-left">
          <h3 className={`font-semibold text-sm ${config.color}`}>
            {config.label}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {config.description}
          </p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.color} border ${config.border}`}>
          {entries.length}
        </span>
        {collapsed
          ? <ChevronRight size={16} className="text-gray-400" />
          : <ChevronDown size={16} className="text-gray-400" />
        }
      </button>
      {!collapsed && (
        <div className="mt-2 space-y-2 pl-2">
          {entries.map(entry => (
            <KBEntry key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Entities tab (new in Phase 4) ──────────────────────────────────────────
//
// Lists every unique (entity_type, entity_id) that has at least one linked
// KB entry, aggregated from the same `entries` array the Knowledge tab uses
// so we do not need a second API call for the structure. Display names are
// resolved with a single batched call to get_entity_display_names, cached
// in localStorage for 24h so repeat visits render instantly.
const DISPLAY_NAME_CACHE_KEY = 'kb_entity_display_names_v1'
const DISPLAY_NAME_TTL_MS = 24 * 60 * 60 * 1000

function loadNameCache() {
  try {
    const raw = localStorage.getItem(DISPLAY_NAME_CACHE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    const now = Date.now()
    // Drop expired entries up front so the in-memory map stays clean
    const out = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (v && v.ts && (now - v.ts) < DISPLAY_NAME_TTL_MS) out[k] = v
    }
    return out
  } catch { return {} }
}

function saveNameCache(cache) {
  try { localStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(cache)) } catch { /* quota — ignore */ }
}

function EntityList({ entries, onSearch }) {
  const { execute } = useTools()
  const [displayNames, setDisplayNames] = useState(() => loadNameCache())

  // Group entries by entity. Unlinked entries (no entity_type/id) collect
  // under a single "Company-wide" bucket so we still surface them somewhere.
  // The `sample` deliberately skips the auto-synthesis row — synthesis text
  // is meta-content, not a user-readable subtitle.
  const grouped = useMemo(() => {
    const m = new Map()
    let unlinkedCount = 0
    for (const e of entries) {
      const t = e.entity_type
      const i = e.entity_id
      if (!t || !i) { unlinkedCount += 1; continue }
      const key = `${t}/${i}`
      if (!m.has(key)) m.set(key, { type: t, id: i, count: 0, last: null, sample: null })
      const g = m.get(key)
      const ts = e.updated_at || e.created_at
      if (ts && (!g.last || new Date(ts) > new Date(g.last))) g.last = ts
      // __synthesis__ rows are meta-content — they should not contribute
      // to the visible count (hub page already excludes them from its own
      // direct_entries list, so the list/detail counts must match) or to
      // the subtitle sample (synthesis text reads as bot output, not user
      // knowledge). They DO contribute to `last` so the row still shows
      // when the synthesis last refreshed.
      const isSynthesis = (e.key || '') === '__synthesis__'
      if (!isSynthesis) {
        g.count += 1
        const sampleTs = g.sample ? (g.sample.updated_at || g.sample.created_at) : null
        if (!g.sample || (ts && (!sampleTs || new Date(ts) > new Date(sampleTs)))) {
          g.sample = e
        }
      }
    }
    const byType = {}
    for (const g of m.values()) {
      if (!byType[g.type]) byType[g.type] = []
      byType[g.type].push(g)
    }
    for (const t of Object.keys(byType)) {
      byType[t].sort((a, b) => b.count - a.count || (new Date(b.last || 0) - new Date(a.last || 0)))
    }
    return { byType, unlinkedCount }
  }, [entries])

  // Resolve display names for any entity not already cached. Single batched
  // tool call — Epicor lookup is fanned out concurrently server-side.
  useEffect(() => {
    const missing = []
    for (const type of Object.keys(grouped.byType)) {
      for (const row of grouped.byType[type]) {
        const k = `${row.type}/${row.id}`
        if (!displayNames[k]) missing.push({ entity_type: row.type, entity_id: row.id })
      }
    }
    if (missing.length === 0) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await execute('get_entity_display_names', { entities: missing })
        const results = res?.result?.results
        if (!results || cancelled) return
        const next = { ...displayNames }
        const now = Date.now()
        for (const r of results) {
          const k = `${r.entity_type}/${r.entity_id}`
          next[k] = {
            name: r.display_name,
            found: r.found,
            ts: now,
          }
        }
        setDisplayNames(next)
        saveNameCache(next)
      } catch (err) {
        // Silent failure — labels stay as "Customer #NNN", still functional
        console.warn('entity name resolution failed', err)
      }
    })()
    return () => { cancelled = true }
    // Intentionally NOT depending on displayNames — re-running on its own
    // updates would cause an infinite loop. The `missing` calc above is the
    // canonical "what is new since last render" signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, execute])

  const typeOrder = ['customer', 'vendor', 'project', 'job', 'part', 'employee']
  const presentTypes = typeOrder.filter(t => grouped.byType[t]?.length)

  if (presentTypes.length === 0) {
    return (
      <div className="text-center py-16">
        <Building2 size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
          No entities with knowledge yet
        </h3>
        <p className="text-sm text-gray-400 dark:text-gray-500 max-w-md mx-auto">
          As you teach Trendy about customers, vendors, jobs, and parts,
          they will appear here. Each entry you save with an entity link
          shows up as a clickable hub.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="p-3 rounded-lg bg-gray-50 dark:bg-surface-dark-raised border border-gray-100 dark:border-gray-800">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Every entity Trendy has stored facts about. Click any row to open
          its hub — the page-per-thing view with live Epicor data, saved
          facts, recent activity, and 1-hop relationships.
        </p>
      </div>

      {presentTypes.map(t => {
        const meta = ENTITY_TYPE_META[t]
        const TypeIcon = meta.icon
        const rows = grouped.byType[t]
        return (
          <div key={t}>
            <div className="flex items-center gap-2 mb-2">
              <TypeIcon size={14} className={meta.iconText} />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {meta.label}s
              </h3>
              <span className="text-xs text-gray-400 dark:text-gray-500">({rows.length})</span>
            </div>
            <div className="space-y-1">
              {rows.map(r => {
                // Primary label: resolved Epicor name when available, else
                // falls back to "Customer #NNN" so the row is never blank.
                const cached = displayNames[`${r.type}/${r.id}`]
                const resolvedName = cached?.name && cached.name !== r.id ? cached.name : null
                const primary = resolvedName || `${meta.label} #${r.id}`
                // Secondary label when we have a real name: subtle type + id.
                // When we do NOT have a name, fall back to the sample entry
                // key (skipping __synthesis__) so the row still has context.
                const secondary = resolvedName
                  ? `${meta.label} · ${r.id}`
                  : (r.sample?.key || `${meta.label} · ${r.id}`)
                return (
                  <Link
                    key={`${r.type}/${r.id}`}
                    to={`/knowledge/entity/${r.type}/${encodeURIComponent(r.id)}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-amtrend-blue/40 hover:bg-amtrend-blue/5 transition-colors group"
                  >
                    <div className={`w-8 h-8 ${meta.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <TypeIcon size={14} className={meta.iconText} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {primary}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {secondary}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 text-right flex-shrink-0">
                      <div>{r.count} {r.count === 1 ? 'entry' : 'entries'}</div>
                      {r.last && <div className="text-[10px]">{timeAgo(r.last)}</div>}
                    </div>
                    <ArrowRight size={14} className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </Link>
                )
              })}
            </div>
          </div>
        )
      })}

      {grouped.unlinkedCount > 0 && (
        <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={() => onSearch('')}
            className="w-full text-left p-2.5 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-amtrend-blue/40 hover:bg-amtrend-blue/5 transition-colors group flex items-center gap-3"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
              <BookOpen size={14} className="text-gray-500 dark:text-gray-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Company-wide knowledge
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Rules and notes that apply across all entities
              </div>
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">{grouped.unlinkedCount}</span>
          </button>
        </div>
      )}
    </div>
  )
}


// ─── Sidebar nav (Notion-style, replaces the old top-tab strip) ─────────────
//
// Two sections:
//   - KNOWLEDGE: Entities (new), Knowledge (was Company Knowledge), How To
//     (was Guides). Available to all roles.
//   - ADMIN: Data Sources, BAQs, Audit. Gated to admin/executive.
function KbSidebar({
  active, onChange, isAdmin,
  entityCount, knowledgeCount, guideCount,
}) {
  const groups = [
    {
      label: 'Knowledge',
      items: [
        { id: 'entities', label: 'Entities', icon: Network, count: entityCount },
        { id: 'knowledge', label: 'Knowledge', icon: Lightbulb, count: knowledgeCount },
        { id: 'guides', label: 'How To', icon: BookOpen, count: guideCount },
      ],
    },
  ]
  if (isAdmin) {
    groups.push({
      label: 'Admin',
      items: [
        { id: 'sources', label: 'Data Sources', icon: Database },
        { id: 'baqs', label: 'BAQs', icon: ListChecks },
        { id: 'audit', label: 'Audit', icon: Activity },
      ],
    })
  }

  return (
    <nav className="space-y-5">
      {groups.map(g => (
        <div key={g.label}>
          <div className="flex items-center gap-1.5 mb-1.5 px-2">
            {g.label === 'Admin' && <Shield size={10} className="text-gray-400" />}
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {g.label}
            </div>
          </div>
          <div className="space-y-0.5">
            {g.items.map(item => {
              const Icon = item.icon
              const isActive = active === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => onChange(item.id)}
                  className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'bg-amtrend-blue/10 text-amtrend-blue font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon size={14} className={isActive ? 'text-amtrend-blue' : 'text-gray-400'} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.count != null && item.count > 0 && (
                    <span className={`text-[11px] ${isActive ? 'text-amtrend-blue/70' : 'text-gray-400 dark:text-gray-500'}`}>
                      {item.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </nav>
  )
}


// ─── Main Page ──────────────────────────────────────────────────────────────

export default function KnowledgeBase() {
  const { execute } = useTools()
  const navigate = useNavigate()
  const tryInChat = useTryInChat()
  const { user } = useAuth()
  const isAdmin = ['admin', 'executive'].includes((user?.role || '').toLowerCase())
  const [entries, setEntries] = useState([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Phase 4: default landing tab is 'entities' — the new discovery surface
  // that lists every entity Trendy knows things about. Old 5-tab order
  // (guides/sources/baqs/knowledge/audit) is preserved as section IDs so
  // links from older sessions still resolve, but the sidebar reorganizes
  // them into Knowledge (entities/knowledge/guides) + Admin (rest).
  const [activeTab, setActiveTab] = useState('entities')

  // Defense in depth: if a non-admin somehow lands on an admin section
  // (e.g., bookmarked URL hash, future hash-based routing), bump back
  // to the entities tab instead of rendering nothing.
  useEffect(() => {
    if (!isAdmin && ['sources', 'baqs', 'audit'].includes(activeTab)) {
      setActiveTab('entities')
    }
  }, [isAdmin, activeTab])

  useEffect(() => {
    loadEntries()
  }, [])

  const loadEntries = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await execute('search_knowledge', { limit: 500 })
      if (res?.result?.entries) {
        setEntries(res.result.entries)
      } else if (res?.result?.error) {
        setError(res.result.error)
      }
    } catch (err) {
      console.error('Failed to load knowledge base:', err)
      setError('Failed to load knowledge base entries')
    } finally {
      setIsLoading(false)
    }
  }

  // Search with server-side full-text search
  const handleSearch = async () => {
    if (!search.trim()) { loadEntries(); return }
    setIsLoading(true)
    try {
      const res = await execute('search_knowledge', { query: search.trim(), limit: 500 })
      if (res?.result?.entries) setEntries(res.result.entries)
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Filter workflow sections by search
  const filteredSections = useMemo(() => {
    if (!search.trim()) return WORKFLOW_SECTIONS
    const q = search.toLowerCase()
    return WORKFLOW_SECTIONS.map(section => ({
      ...section,
      articles: section.articles.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.prompts.some(p => p.toLowerCase().includes(q)) ||
        (a.tips || '').toLowerCase().includes(q)
      ),
    })).filter(s => s.articles.length > 0)
  }, [search])

  // Filter dynamic entries by search
  const filteredEntries = useMemo(() => {
    if (!search.trim()) return entries
    const q = search.toLowerCase()
    return entries.filter(e =>
      (e.key || '').toLowerCase().includes(q) ||
      (e.value || '').toLowerCase().includes(q) ||
      (e.category || '').toLowerCase().includes(q) ||
      (e.entity_id || '').toLowerCase().includes(q)
    )
  }, [entries, search])

  // Group dynamic entries by category
  const grouped = useMemo(() => {
    const groups = {}
    for (const entry of filteredEntries) {
      const cat = entry.category || 'other'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(entry)
    }
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
    }
    return groups
  }, [filteredEntries])

  const categoryOrder = useMemo(() => {
    const cats = Object.keys(grouped)
    const priority = ['business_rule', 'customer_note', 'part_note', 'pricing_note', 'entity_relationship']
    return priority.filter(c => cats.includes(c)).concat(cats.filter(c => !priority.includes(c)))
  }, [grouped])

  const totalArticles = WORKFLOW_SECTIONS.reduce((sum, s) => sum + s.articles.length, 0)

  // Distinct entity count for the sidebar badge. Cheap — entries are already
  // in memory and the Set keeps it linear.
  const entityCount = useMemo(() => {
    const s = new Set()
    for (const e of entries) {
      if (e.entity_type && e.entity_id) s.add(`${e.entity_type}/${e.entity_id}`)
    }
    return s.size
  }, [entries])

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amtrend-blue/10 rounded-lg">
              <BookOpen size={24} className="text-amtrend-blue" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Knowledge Base</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Everything Trendy knows about Amtrend, organized for browsing
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amtrend-blue hover:bg-amtrend-blue/10 transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Trendy</span>
          </button>
        </div>

        {/* 2-column shell: sidebar nav (left) + content (right) */}
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 md:gap-8">
          {/* Sidebar */}
          <aside className="md:sticky md:top-6 md:self-start">
            {/* Search lives in the sidebar — filters whichever tab is active */}
            <div className="mb-5">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-8 pr-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/30 focus:border-amtrend-blue"
                />
              </div>
            </div>

            <KbSidebar
              active={activeTab}
              onChange={setActiveTab}
              isAdmin={isAdmin}
              entityCount={entityCount}
              knowledgeCount={filteredEntries.length}
              guideCount={totalArticles}
            />

            {/* Popular searches — only when search box is empty */}
            {!search && (
              <div className="mt-6">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 px-2">
                  Popular
                </div>
                <div className="flex flex-wrap gap-1 px-2">
                  {['credit hold', 'shipping invoice', 'AR aging', 'quote to order'].map(tag => (
                    <button
                      key={tag}
                      onClick={() => setSearch(tag)}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-amtrend-blue/10 hover:text-amtrend-blue transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* Main panel */}
          <main className="min-w-0">

        {/* ─── ENTITIES TAB (new — Phase 4) ─── */}
        {activeTab === 'entities' && (
          <EntityList entries={filteredEntries} onSearch={setSearch} />
        )}

        {/* ─── DATA SOURCES TAB (admin-only) ─── */}
        {activeTab === 'sources' && isAdmin && (
          <DataSourcesTab />
        )}

        {/* ─── BAQs TAB (admin-only) — browsable BAQ roster + self-documentation ─── */}
        {activeTab === 'baqs' && isAdmin && (
          <BAQsTab onTryInChat={tryInChat} />
        )}

        {/* ─── AUDIT TAB (admin-only) ─── */}
        {activeTab === 'audit' && isAdmin && (
          <AuditTab />
        )}

        {/* ─── GUIDES TAB (renamed "How To" in the sidebar) ─── */}
        {activeTab === 'guides' && (
          <>
            {/* Onboarding */}
            {!search && <OnboardingCards onTry={tryInChat} />}

            {/* Prompting tips */}
            {!search && <PromptingTips />}

            {/* Workflow sections */}
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <span>I want to...</span>
                <span className="text-xs font-normal text-gray-400">
                  Click a section, then click any prompt to try it in chat
                </span>
              </h2>
            </div>

            {filteredSections.length === 0 && search && (
              <div className="text-center py-12">
                <Search size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No guides match &ldquo;{search}&rdquo;
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Try searching Company Knowledge tab, or ask Trendy directly
                </p>
              </div>
            )}

            {filteredSections.map((section, i) => (
              <WorkflowSection
                key={section.id}
                section={section}
                onTry={tryInChat}
                defaultExpanded={search.trim().length > 0 || i === 0}
              />
            ))}
          </>
        )}

        {/* ─── COMPANY KNOWLEDGE TAB ─── */}
        {activeTab === 'knowledge' && (
          <>
            <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-surface-dark-raised border border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                These entries are learned by Trendy from your conversations and manual additions.
                They help Trendy give accurate, company-specific answers.
              </p>
            </div>

            {/* Two-column document search across Trendy's sources (Epicor
                attachments + Outlook emails). Renders only when there's a
                query — otherwise stays out of the way of curated entries. */}
            <SourceSearchResults query={search} />

            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={32} className="animate-spin text-amtrend-blue" />
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-3">
                <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {!isLoading && !error && filteredEntries.length === 0 && (
              <div className="text-center py-16">
                <BookOpen size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                  {search ? 'No matching entries' : 'No company knowledge yet'}
                </h3>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {search
                    ? 'Try a different search term.'
                    : 'Ask Trendy to remember things — e.g., "Remember that PMI requires FOB Fullerton."'
                  }
                </p>
              </div>
            )}

            {!isLoading && categoryOrder.map(cat => {
              const config = CATEGORY_CONFIG[cat] || {
                label: cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                description: '',
                color: 'text-gray-600 dark:text-gray-400',
                bg: 'bg-gray-50 dark:bg-gray-900/20',
                border: 'border-gray-200 dark:border-gray-700',
              }
              return (
                <DynamicKBSection
                  key={cat}
                  category={cat}
                  entries={grouped[cat]}
                  config={config}
                />
              )
            })}
          </>
        )}

          </main>
        </div>
      </div>
    </div>
  )
}
